import { isEqual, lt, some } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import { Button, Stack, Tooltip } from '@mui/material';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import axiosInstance from 'src/utils/axios';
import { TAB_QUERY } from 'src/utils/common-types';
import { hasPermission } from 'src/utils/has-permissions';
import logToucodeEvent, { APP_EVENTS } from 'src/utils/log-google-events';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { KITCHEN_CATEGORY_ID } from 'src/config-global';
import { getPaginatedUnits } from 'src/redux/slices/units';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteInventory,
  disableKitchenItem,
  getInventory,
  removeKitchenItem,
} from 'src/redux/slices/kitchen';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IKitchenListItem,
  IKitchenTableFilters,
  IKitchenTableFilterValue,
  IKitchenTableSort,
  KITCHEN_STATUS_OPTIONS,
  KitchenAlertStatus,
  KitchenStatus,
} from 'src/types/kitchen';

import InventoryTableRow from '../kitchen-table-row';
import KitchenTableToolbar from '../kitchen-table-toolbar';

const STATUS_OPTIONS = [{ value: 'all', label: 'All' }, ...KITCHEN_STATUS_OPTIONS];

const TABLE_HEAD = [
  { id: 'expand', primary: true },
  { id: 'itemName', label: 'Item Name', align: 'left', primary: true },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
  { id: 'availability', label: 'Availability', align: 'left', primary: true },
  { id: 'updatedAt', label: 'Updated', align: 'left', primary: true },
  { id: '', primary: true },
];

const defaultFilters: IKitchenTableFilters = {
  generalSearch: '',
  units: [],
  alertsEnabled: KitchenAlertStatus.ALL,
  minQuantity: null,
  maxQuantity: null,
  expiringSoon: false,
  expired: false,
};

const EXPIRING_SOON_QUERY = 'expiringSoon';
const EXPIRED_QUERY = 'expired';

const defaultSortBy: IKitchenTableSort = {
  sortBy: 'updatedAt',
  order: 'desc',
};

const disablingKitchenItemInitialState: {
  state: boolean;
  index: number | null;
  message: string;
  item: IKitchenListItem | null;
} = {
  state: false,
  index: null,
  message: '',
  item: null,
};

export default function KitchenListView() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTab = searchParams.get(TAB_QUERY);

  const { status, inventory, error } = useAppSelector((state) => state.inventory);

  const { units } = useAppSelector((state) => state.units);

  const { user: authUser } = useAuthContext();

  const table = useTable({
    defaultRowsPerPage: 25,
    defaultDense: true,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [disablingKitchenItem, setDisablingKitchenItem] = useState(
    disablingKitchenItemInitialState
  );

  const [filters, setFilters] = useState<IKitchenTableFilters>(() => ({
    ...defaultFilters,
    expiringSoon: searchParams.get(EXPIRING_SOON_QUERY) === 'true',
    expired: searchParams.get(EXPIRED_QUERY) === 'true',
  }));

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const activeFilterCount = (Object.keys(defaultFilters) as (keyof IKitchenTableFilters)[]).filter(
    (key) => key !== 'generalSearch' && !isEqual(filters[key], defaultFilters[key])
  ).length;

  const [statusTab, setStatusTab] = useState<string>(currentTab || KitchenStatus.ALL);

  const handleFilters = useCallback(
    (name: string, value: IKitchenTableFilterValue) => {
      table.onResetPage();

      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.kitchen.edit(id));
    },
    [router]
  );

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      setStatusTab(newValue);

      setSearchParams({ [TAB_QUERY]: newValue });
    },
    [setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    setSearchParams({ [TAB_QUERY]: KitchenStatus.ALL });
    setFilters(defaultFilters);
  }, [setSearchParams]);

  const fetchPaginatedKitchenItems = useCallback(() => {
    if (
      some(
        [filters.minQuantity, filters.maxQuantity],
        (value) => value !== null && Number(value) < 0
      ) ||
      (filters.minQuantity !== null &&
        filters.maxQuantity !== null &&
        lt(Number(filters.maxQuantity), Number(filters.minQuantity)))
    ) {
      return;
    }

    dispatch(
      getInventory({
        page,
        limit: rowsPerPage,
        sortBy: sort.sortBy,
        sort: sort.order,
        generalSearch: filters.generalSearch,
        availability: statusTab === KitchenStatus.ALL ? null : statusTab,
        units: filters.units,
        unitsSeparator: ',',
        alertEnabled:
          filters.alertsEnabled === KitchenAlertStatus.ALL
            ? null
            : filters.alertsEnabled === KitchenAlertStatus.ENABLED,
        minQuantity: filters.minQuantity,
        maxQuantity: filters.maxQuantity,
        expiringSoon: filters.expiringSoon,
        expired: filters.expired,
      })
    );

    if (page > 1) {
      logToucodeEvent(APP_EVENTS.TABLE_PAGINATION_MORE_THAN_TWO, {
        page,
        rowsPerPage,
      });
    }
  }, [dispatch, page, rowsPerPage, sort, filters, statusTab]);

  const handleDeleteRow = useCallback(
    async (item: IKitchenListItem, index: number) => {
      dispatch(removeKitchenItem({ itemId: item._id }));

      try {
        await dispatch(deleteInventory({ item, index })).unwrap();

        fetchPaginatedKitchenItems();
        enqueueSnackbar('Inventory item deleted');
      } catch (e) {
        if ('name' in e && e.name === 'AxiosError') {
          setDisablingKitchenItem({
            state: true,
            message: e.message,
            item,
            index,
          });
        } else if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedKitchenItems]
  );

  const handleDisableRow = useCallback(
    async (item: IKitchenListItem, index: number) => {
      dispatch(removeKitchenItem({ itemId: item._id }));

      try {
        await dispatch(disableKitchenItem({ item, index })).unwrap();

        fetchPaginatedKitchenItems();
        enqueueSnackbar('Inventory item disabled');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedKitchenItems]
  );

  const downloadKitchenReport = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `inventory/generate-inventory-report?page=0&limit=0&generalSearch=${
          filters.generalSearch
        }&itemCode=&itemName=&minQuantity=${filters.minQuantity ?? ''}&maxQuantity=${
          filters.minQuantity ?? ''
        }&minPrice=&maxPrice=&suppliers=&categories=${KITCHEN_CATEGORY_ID}&units=${filters.units.join(
          ','
        )}&group=&availability=${statusTab === KitchenStatus.ALL ? '' : statusTab}&alertEnabled=${
          filters.alertsEnabled === KitchenAlertStatus.ALL
            ? ''
            : filters.alertsEnabled === KitchenAlertStatus.ENABLED
        }&alertSent=&sortBy=itemName&sort=asc&status=&createdBy=&locations=&itemCategories=`,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/pdf',
          },
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'kitchen-stock-report.pdf';
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error('Error downloading the PDF:', e);
    }
  }, [filters, statusTab]);

  const fetchUnits = useCallback(() => {
    dispatch(getPaginatedUnits({}));
  }, [dispatch]);

  useEffect(() => {
    fetchPaginatedKitchenItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedKitchenItems]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  return (
    <>
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Inventory Items"
          links={[]}
          action={
            <Stack direction="row" gap={2}>
              {hasPermission(
                authUser,
                'any',
                AuthPermissions.DOWNLOAD_INVENTORY_REPORTS,
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              ) && (
                <Tooltip title="Download Inventory Report">
                  <Button variant="outlined" color="primary" onClick={downloadKitchenReport}>
                    <Iconify icon="eva:arrow-circle-down-fill" width={24} />
                  </Button>
                </Tooltip>
              )}

              {hasPermission(
                authUser,
                'any',
                AuthPermissions.CREATE_KITCHEN_ITEM,
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              ) && (
                <Button
                  component={RouterLink}
                  href={paths.dashboard.kitchen.new}
                  variant="contained"
                  startIcon={<Iconify icon="mingcute:add-line" />}
                >
                  New Inventory Item
                </Button>
              )}
            </Stack>
          }
          sx={{
            mb: { xs: 3 },
          }}
        />

        <Card>
          <Tabs
            value={statusTab}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {STATUS_OPTIONS.map((tab) => (
              <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          <KitchenTableToolbar
            filters={filters}
            onFilters={handleFilters}
            units={units.units}
            handleResetFilters={handleResetFilters}
            canReset={canReset}
            activeFilterCount={activeFilterCount}
          />

          <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                <TableHeadCustom
                  order={sort.order}
                  orderBy={sort.sortBy}
                  headLabel={TABLE_HEAD.filter(({ primary }) => {
                    if (settings.themeLayout === 'vertical') {
                      return primary;
                    }
                    return true;
                  })}
                  rowCount={inventory.items.length}
                  numSelected={table.selected.length}
                  onSort={(id) => {
                    if (id === sort.sortBy) {
                      setSort((prev) => ({
                        ...prev,
                        order: prev.order === 'asc' ? 'desc' : 'asc',
                      }));
                    } else {
                      setSort({
                        sortBy: id,
                        order: 'asc',
                      });
                    }
                  }}
                />

                <TableBody>
                  {status === 'loading' ? (
                    [...Array(table.rowsPerPage)].map((i, index) => (
                      <TableSkeleton key={index} sx={{ height: denseHeight }} />
                    ))
                  ) : (
                    <>
                      {inventory?.items.map((row: IKitchenListItem, index: number) => (
                        <InventoryTableRow
                          key={row._id}
                          row={row}
                          selected={table.selected.includes(row._id)}
                          onDeleteRow={() => handleDeleteRow(row, index)}
                          onEditRow={() => handleEditRow(row._id)}
                        />
                      ))}
                    </>
                  )}

                  {status !== 'failure' && inventory.count === 0 && (
                    <TableNoData
                      message="No inventory items found"
                      notFound={inventory.count === 0}
                    />
                  )}

                  {status === 'failure' && <TableErrorData notFound error={error} />}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={inventory.count}
            page={table.page}
            rowsPerPage={table.rowsPerPage}
            onPageChange={table.onChangePage}
            onRowsPerPageChange={table.onChangeRowsPerPage}
            dense={table.dense}
            onChangeDense={(value) => {
              table.onChangeDense(value);

              logToucodeEvent(APP_EVENTS.TABLE_COMPACT_MODE_TOGGLE, {
                compact: value,
                table: 'kitchen_table',
              });
            }}
          />
        </Card>
      </Container>
      <ConfirmDialog
        open={disablingKitchenItem.state}
        onClose={() => setDisablingKitchenItem(disablingKitchenItemInitialState)}
        title="Disable Inventory Item"
        content={disablingKitchenItem.message}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (disablingKitchenItem.item !== null && disablingKitchenItem.index !== null) {
                handleDisableRow(disablingKitchenItem.item, disablingKitchenItem.index);
                setDisablingKitchenItem(disablingKitchenItemInitialState);
              }
            }}
          >
            Disable
          </Button>
        }
      />
    </>
  );
}
